import React, { useState, useEffect, useCallback } from 'react';
import { YearGroup, CurriculumSubject, CurriculumTopic, PreGeneratedCard } from '../../types/Curriculum';
import { curriculumApi } from '../../services/curriculumApi';
import { useAuth } from '../../contexts/AuthContext';
import './CurriculumBrowser.css';

interface CurriculumBrowserProps {
  onGenerateCards: (topics: CurriculumTopic[], selectedCards: PreGeneratedCard[]) => void;
  onShowAuth: (reason: string) => void;
}

export const CurriculumBrowser: React.FC<CurriculumBrowserProps> = ({
  onGenerateCards,
  onShowAuth,
}) => {
  const { isAuthenticated, quota } = useAuth();

  const [years, setYears] = useState<YearGroup[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [subjects, setSubjects] = useState<CurriculumSubject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [topics, setTopics] = useState<CurriculumTopic[]>([]);
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);
  const [topicCards, setTopicCards] = useState<Record<string, PreGeneratedCard[]>>({});
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(new Set());
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    curriculumApi.getYears()
      .then(setYears)
      .catch(() => setError('Could not load year groups.'));
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setIsLoading(true);
    setSubjects([]);
    setSelectedSubject(null);
    setTopics([]);
    curriculumApi.getSubjects(selectedYear)
      .then(setSubjects)
      .catch(() => setError('Could not load subjects.'))
      .finally(() => setIsLoading(false));
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedYear || !selectedSubject) return;
    setIsLoading(true);
    setTopics([]);
    setSelectedTopicIds(new Set());
    setSelectedCardIds(new Set());
    curriculumApi.getTopics(selectedYear, selectedSubject)
      .then(setTopics)
      .catch(() => setError('Could not load topics.'))
      .finally(() => setIsLoading(false));
  }, [selectedYear, selectedSubject]);

  const loadTopicCards = useCallback(async (topicId: string) => {
    if (topicCards[topicId]) return;
    const cards = await curriculumApi.getCards(topicId);
    setTopicCards(prev => ({ ...prev, [topicId]: cards }));
  }, [topicCards]);

  const toggleTopic = (topicId: string) => {
    if (expandedTopic === topicId) {
      setExpandedTopic(null);
    } else {
      setExpandedTopic(topicId);
      loadTopicCards(topicId);
    }
  };

  const toggleTopicSelection = (topicId: string) => {
    setSelectedTopicIds(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
        // deselect all cards for this topic
        const cards = topicCards[topicId] || [];
        setSelectedCardIds(prev2 => {
          const n2 = new Set(prev2);
          cards.forEach(c => n2.delete(c.cardId));
          return n2;
        });
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const toggleCardSelection = (cardId: string) => {
    setSelectedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) { next.delete(cardId); } else { next.add(cardId); }
      return next;
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGenerate = () => {
    if (!isAuthenticated) {
      onShowAuth('Sign in to generate your brain break cards');
      return;
    }

    const selectedTopics = topics.filter(t => selectedTopicIds.has(t.topicId));
    const selectedCards = Object.values(topicCards)
      .flat()
      .filter(c => selectedCardIds.has(c.cardId));

    setIsSubmitting(true);
    onGenerateCards(selectedTopics, selectedCards);
  };

  const totalSelected = selectedTopicIds.size;
  const quotaRemaining = quota ? quota.generationsLimit - quota.generationsUsed : null;

  return (
    <div className="curriculum-browser">

      {/* Year group picker */}
      <section className="cb-section">
        <h2 className="cb-section-title">Select a year group</h2>
        <div className="cb-year-grid">
          {years.map(y => (
            <button
              key={y.year}
              className={`cb-year-btn ${selectedYear === y.year ? 'selected' : ''}`}
              onClick={() => setSelectedYear(y.year)}
            >
              <span className="cb-year-label">{y.label}</span>
              <span className="cb-year-ks">{y.keyStage}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Subject picker */}
      {selectedYear && !isLoading && subjects.length === 0 && (
        <section className="cb-section">
          <p className="cb-empty">No curriculum content for this year group yet. Try uploading your own syllabus instead.</p>
        </section>
      )}
      {subjects.length > 0 && (
        <section className="cb-section">
          <h2 className="cb-section-title">Choose a subject</h2>
          <div className="cb-subject-grid">
            {subjects.map(s => (
              <button
                key={s.subject}
                className={`cb-subject-btn ${selectedSubject === s.subject ? 'selected' : ''}`}
                onClick={() => setSelectedSubject(s.subject)}
              >
                <span className="cb-subject-emoji">{s.emoji}</span>
                <span className="cb-subject-label">{s.label}</span>
                <span className="cb-subject-count">{s.topicCount} topics</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Topic list */}
      {topics.length > 0 && (
        <section className="cb-section">
          <h2 className="cb-section-title">Pick topics</h2>
          <p className="cb-hint">Select topics, then expand to choose specific questions — or just pick topics and we'll generate fresh cards.</p>
          <div className="cb-topic-list">
            {topics.map(topic => {
              const isSelected = selectedTopicIds.has(topic.topicId);
              const isExpanded = expandedTopic === topic.topicId;
              const cards = topicCards[topic.topicId] || [];

              return (
                <div key={topic.topicId} className={`cb-topic ${isSelected ? 'selected' : ''}`}>
                  <div className="cb-topic-header">
                    <label className="cb-topic-check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTopicSelection(topic.topicId)}
                      />
                      <span className="cb-topic-name">{topic.topicName}</span>
                    </label>
                    <button
                      className="cb-topic-expand"
                      onClick={() => toggleTopic(topic.topicId)}
                      title={isExpanded ? 'Hide sample questions' : 'Preview sample questions'}
                    >
                      {isExpanded ? '▲ Hide' : '▼ Preview questions'}
                    </button>
                  </div>

                  {topic.keyConcepts.length > 0 && (
                    <div className="cb-concepts">
                      {topic.keyConcepts.map(k => (
                        <span key={k} className="cb-concept-chip">{k}</span>
                      ))}
                    </div>
                  )}

                  {isExpanded && (
                    <div className="cb-cards-preview">
                      {cards.length === 0 ? (
                        <p className="cb-loading">Loading questions…</p>
                      ) : (
                        cards.map(card => (
                          <label key={card.cardId} className="cb-card-option">
                            <input
                              type="checkbox"
                              checked={selectedCardIds.has(card.cardId)}
                              onChange={() => toggleCardSelection(card.cardId)}
                            />
                            <div className="cb-card-prompts">
                              <div className="cb-prompt">
                                <span className="cb-prompt-type">Question</span>
                                {card.prompts.question}
                              </div>
                              <div className="cb-prompt">
                                <span className="cb-prompt-type">Wondering</span>
                                {card.prompts.wonder}
                              </div>
                              <div className="cb-prompt">
                                <span className="cb-prompt-type">Investigate</span>
                                {card.prompts.investigate}
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {isLoading && <p className="cb-loading-global">Loading…</p>}
      {error && <p className="cb-error">{error}</p>}

      {/* Generate bar */}
      {totalSelected > 0 && (
        <div className="cb-generate-bar">
          <div className="cb-generate-info">
            <span>{totalSelected} topic{totalSelected !== 1 ? 's' : ''} selected</span>
            {selectedCardIds.size > 0 && (
              <span className="cb-card-count">{selectedCardIds.size} specific question{selectedCardIds.size !== 1 ? 's' : ''} chosen</span>
            )}
            {quotaRemaining !== null && (
              <span className="cb-quota">{quotaRemaining} generation{quotaRemaining !== 1 ? 's' : ''} remaining this month</span>
            )}
          </div>
          <button
            className="cb-generate-btn"
            onClick={handleGenerate}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Generating…' : isAuthenticated ? 'Generate Cards' : 'Sign in to Generate'}
          </button>
        </div>
      )}
    </div>
  );
};
